(() => {
  const SCROLLER_SELECTOR = '[id^="Slider-"], zoria-community-stories';
  const FOCUSABLE_SELECTOR = 'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]';
  const itemState = new WeakMap();
  const focusState = new WeakMap();
  const observedScrollers = new WeakSet();
  let frame = 0;

  const restoreAttribute = (element, name, value) => {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  };

  const setFocusableState = (element, unavailable) => {
    if (unavailable) {
      if (!focusState.has(element)) {
        focusState.set(element, {
          tabIndex: element.getAttribute('tabindex'),
          ariaHidden: element.getAttribute('aria-hidden'),
        });
      }
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-hidden', 'true');
      element.dataset.zoriaCarouselFocusHidden = 'true';
      return;
    }

    const previous = focusState.get(element);
    if (!previous) return;
    restoreAttribute(element, 'tabindex', previous.tabIndex);
    restoreAttribute(element, 'aria-hidden', previous.ariaHidden);
    delete element.dataset.zoriaCarouselFocusHidden;
    focusState.delete(element);
  };

  const setItemState = (item, unavailable) => {
    if (unavailable) {
      if (!itemState.has(item)) {
        itemState.set(item, {
          inert: item.hasAttribute('inert'),
          ariaHidden: item.getAttribute('aria-hidden'),
        });
      }
      item.setAttribute('inert', '');
      item.setAttribute('aria-hidden', 'true');
      item.dataset.zoriaCarouselOffscreen = 'true';
      return;
    }

    const previous = itemState.get(item);
    if (!previous) return;
    if (!previous.inert) item.removeAttribute('inert');
    restoreAttribute(item, 'aria-hidden', previous.ariaHidden);
    delete item.dataset.zoriaCarouselOffscreen;
    itemState.delete(item);
  };

  const isIntrinsicallyHidden = (item) => {
    const previous = itemState.get(item);
    const ariaHidden = previous ? previous.ariaHidden : item.getAttribute('aria-hidden');
    const style = getComputedStyle(item);
    return item.hidden || item.classList.contains('hidden') || ariaHidden === 'true' || style.display === 'none' || style.visibility === 'hidden';
  };

  const itemCandidates = (scroller) => {
    if (scroller.matches('zoria-community-stories')) {
      return Array.from(scroller.querySelectorAll(':scope > [data-story-card]'));
    }
    return Array.from(scroller.children).filter((item) => item.matches('[id^="Slide-"], .slider__slide, .z-products__slide'));
  };

  const outsideHorizontalClip = (rect, clip) => rect.right <= clip.left + 1 || rect.left >= clip.right - 1;

  const syncScroller = (scroller) => {
    if (!(scroller instanceof HTMLElement) || !scroller.isConnected) return;
    const items = itemCandidates(scroller);
    if (!items.length) return;

    const clip = scroller.getBoundingClientRect();
    const overflowX = getComputedStyle(scroller).overflowX;
    const canClipHorizontally = ['auto', 'scroll', 'hidden', 'clip'].includes(overflowX) && scroller.scrollWidth > scroller.clientWidth + 2;
    items.forEach((item) => {
      const itemUnavailable = isIntrinsicallyHidden(item) || (canClipHorizontally && outsideHorizontalClip(item.getBoundingClientRect(), clip));
      setItemState(item, itemUnavailable);

      item.querySelectorAll(FOCUSABLE_SELECTOR).forEach((focusable) => {
        const controlUnavailable = itemUnavailable || (canClipHorizontally && outsideHorizontalClip(focusable.getBoundingClientRect(), clip));
        setFocusableState(focusable, controlUnavailable);
      });
    });

    if (!observedScrollers.has(scroller)) {
      observedScrollers.add(scroller);
      resizeObserver.observe(scroller);
    }
  };

  const syncAll = () => {
    frame = 0;
    document.querySelectorAll(SCROLLER_SELECTOR).forEach(syncScroller);
  };

  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(syncAll);
  };

  const resizeObserver = new ResizeObserver(schedule);
  const mutationObserver = new MutationObserver(schedule);

  window.ZoriaCarouselFocus = Object.freeze({ sync: syncScroller, schedule });
  document.addEventListener('scroll', schedule, true);
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', schedule);

  const start = () => {
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
